/**
 * Лаборатория графиков — свободный режим.
 *
 * Исключение из стоп-листа фазы 1, как и атлас: экран ничего не открывает,
 * не назначает и не двигает план. Он отвечает на один вопрос, который по
 * задачам не задашь, — что коэффициент делает с кривой.
 *
 * Шапки у экрана нет вовсе: имя («Лаборатория») и выход стоят в шапке
 * приложения, а здесь сразу панель и плоскость. Сначала заголовок с пояснением
 * занимал верхнюю четверть экрана, потом слово «Графики» внутри рамки — и оно
 * повторяло то, что уже написано в шапке и так видно по осям.
 *
 * Состояние живёт в браузере, а не на сервере: ученик возвращается к своему
 * последнему набору кривых, и ради этого не нужен ни документ, ни подписка.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { GraphCanvas, type CurveTrace } from "../ui/function-lab/GraphCanvas";
import { CurveList } from "../ui/function-lab/CurveList";
import { ValueProbe } from "../ui/function-lab/ValueProbe";
import { ValueTable } from "../ui/function-lab/ValueTable";
import {
  activeCurve, addCurve, parametersOf, parameterValue, removeCurve, replaceCurve, resetCurve,
  setActiveCurve, setParameter, toggleVisible, updateCurve,
  type CurveFamily, type FunctionCurve, type LabScene, type ParameterKey,
} from "../lib/function-lab/model";
import { beginHistory, canUndo, commit, preview, replace, undo } from "../lib/function-lab/history";
import { loadScene, saveScene } from "../lib/function-lab/persistence";
import { curveFeatures, pointText } from "../lib/function-lab/math";
import { valueTable } from "../lib/function-lab/controls";
import { downloadScenePng, readPalette } from "../lib/function-lab/export";
import {
  autoFitPlot, showAll, zoomPlot,
  type PlotCamera, type PlotPoint, type PlotSize,
} from "../lib/function-lab/viewport";
import { formatExact, type ExactNumber } from "../lib/function-lab/numbers";
import "../function-lab.css";

/** Сколько держится бледный след прошлого положения кривой. */
const TRACE_MS = 1600;

/** Точки, которые камера обязана удержать: начало координат и всё характерное. */
function anchors(scene: LabScene): PlotPoint[] {
  const points: PlotPoint[] = [{ x: 0, y: 0 }];
  for (const entry of scene.curves) {
    if (!entry.visible) continue;
    const features = curveFeatures(entry.curve);
    for (const point of features.points) {
      points.push({ x: point.x.value, y: point.y.value });
    }
    // Окружность по центру и пересечениям с осями в кадр целиком не входит.
    points.push(...features.extent);
  }
  return points;
}

function show(value: ExactNumber): string {
  return formatExact(value).replace(/^-/, "−");
}

export function FunctionLab() {
  const [history, setHistory] = useState(() => beginHistory(loadScene()));
  const [size, setSize] = useState<PlotSize>({ width: 0, height: 0 });
  const [offscreen, setOffscreen] = useState<PlotPoint[]>([]);
  const [probeX, setProbeX] = useState<number | null>(null);
  const [tableOpen, setTableOpen] = useState(false);
  const [trace, setTrace] = useState<CurveTrace | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const traceTimer = useRef<number | null>(null);
  const scene = history.present;
  const active = activeCurve(scene);
  const visible = scene.curves.filter((entry) => entry.visible);

  useEffect(() => { saveScene(scene); }, [scene]);
  useEffect(() => () => { if (traceTimer.current !== null) clearTimeout(traceTimer.current); }, []);

  /** Ползунок под пальцем: перерисовать, но историю и камеру не трогать. */
  const live = (next: LabScene) => setHistory((h) => preview(h, next));

  /**
   * Значение отпущено. Камера подстраивается только здесь: пересчитывать её
   * на каждом промежуточном значении значит дёргать плоскость под руками.
   */
  const settle = (next: LabScene) => {
    const fitted = autoFitPlot(next.camera, size, anchors(next));
    setOffscreen(fitted.offscreen);
    setHistory((h) => commit(h, { ...next, camera: fitted.camera }));
  };

  const onCamera = useCallback((camera: PlotCamera) => {
    setHistory((h) => replace(h, { ...h.present, camera }));
  }, []);
  const onSize = useCallback((next: PlotSize) => setSize(next), []);

  /**
   * След прошлого положения. Кривая уезжает мгновенно, и без него виден
   * только результат: куда она переехала — вопрос памяти, а не глаза.
   */
  const leaveTrace = (before: CurveTrace, text: string) => {
    if (traceTimer.current !== null) clearTimeout(traceTimer.current);
    setTrace(before);
    setNote(text);
    traceTimer.current = window.setTimeout(() => {
      setTrace(null);
      setNote(null);
      traceTimer.current = null;
    }, TRACE_MS);
  };

  /**
   * Правка коэффициента любой кривой, не только активной: поля стоят в
   * карточках. Тронутая кривая становится активной — иначе толще на
   * плоскости остаётся соседняя, а подписи точек показывают её.
   */
  const change = (id: string, key: ParameterKey, value: ExactNumber, final: boolean) => {
    const target = scene.curves.find((entry) => entry.id === id);
    if (!target) return;
    const edited = updateCurve(scene, id, (c) => ({
      ...c, curve: setParameter(c.curve, key, value),
    }));
    const next = id === scene.activeId ? edited : setActiveCurve(edited, id);
    if (!final) { live(next); return; }
    const was = history.anchor
      ? history.anchor.curves.find((c) => c.id === id)
      : target;
    const before = was ? parameterValue(was.curve, key) : null;
    if (was && before && show(before) !== show(value)) {
      const symbol = parametersOf(target.curve.kind).find((meta) => meta.key === key)?.symbol ?? key;
      leaveTrace({ curve: was.curve, slot: target.slot }, `${symbol}: ${show(before)} → ${show(value)}`);
    }
    settle(next);
  };

  // Ctrl+Z и Cmd+Z: отмена ожидается по этой комбинации везде, и отдельная
  // кнопка её не отменяет, а дублирует.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "z" && event.key !== "Z" && event.key !== "я" && event.key !== "Я") return;
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.altKey) return;
      event.preventDefault();
      setHistory(undo);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const savePng = async () => {
    setSaving(null);
    try {
      await downloadScenePng(scene, size, readPalette(document.documentElement, 4));
    } catch {
      setSaving("Не удалось сохранить картинку — попробуйте ещё раз.");
    }
  };

  const features = active ? curveFeatures(active.curve) : null;
  const rows = valueTable(scene, scene.camera, size, probeX);
  const zoom = (factor: number) =>
    onCamera(zoomPlot(scene.camera, size, { x: size.width / 2, y: size.height / 2 }, factor));

  return <section className="fn-lab card">
    <div className="fn-lab__body">
      <div className="fn-lab__panel">
        <CurveList scene={scene}
          onSelect={(id) => setHistory((h) => replace(h, setActiveCurve(h.present, id)))}
          onToggle={(id) => settle(toggleVisible(scene, id))}
          onRemove={(id) => settle(removeCurve(scene, id))}
          onReset={(id) => settle(resetCurve(scene, id))}
          onAdd={(family: CurveFamily) => settle(addCurve(scene, family))}
          onParameter={change}
          onConvert={(id: string, curve: FunctionCurve) => settle(replaceCurve(scene, id, curve))} />

        {active ? <>
          <h2 className="fn-lab__section">Характерные точки</h2>
          <ul className="fn-lab__points">
            {features && features.points.length > 0
              ? features.points.map((point, index) => (
                <li key={index}><span>{point.label}</span><b>{pointText(point)}</b></li>
              ))
              : <li className="fn-lab__empty">Пересечений с осями нет</li>}
          </ul>
          <div className="fn-lab__actions">
            <button className="btn btn--quiet" disabled={!canUndo(history)}
              onClick={() => setHistory(undo)}>Отменить</button>
          </div>
        </> : null}
      </div>

      <div className="fn-lab__stage">
        <GraphCanvas curves={visible} activeId={scene.activeId}
          camera={scene.camera} size={size} offscreen={offscreen} probeX={probeX} trace={trace}
          onCamera={onCamera} onSize={onSize} onProbe={setProbeX} />
        {note ? <p className="fn-lab__note-change" role="status">{note}</p> : null}
        <div className="fn-lab__camera">
          <button className="btn" aria-label="Уменьшить масштаб" onClick={() => zoom(0.8)}>−</button>
          <span>{Math.round(scene.camera.scale)} px на единицу</span>
          <button className="btn" aria-label="Увеличить масштаб" onClick={() => zoom(1.25)}>+</button>
          <button className="btn" onClick={() => {
            onCamera(showAll(anchors(scene), size));
            setOffscreen([]);
          }}>Показать всё</button>
          <button className="btn btn--quiet" onClick={savePng}>Скачать картинку</button>
        </div>
        {saving ? <p className="fn-lab__param-error" role="alert">{saving}</p> : null}
        <ValueProbe scene={scene} x={probeX} />
        <ValueTable rows={rows} curves={visible} open={tableOpen}
          onToggle={() => setTableOpen((open) => !open)} />
      </div>
    </div>
  </section>;
}
